/**
 * Shared React context for the platform pool player. The chunk-aware state
 * machine (`./core`) and seek hooks (`./hooks`) read the player through this
 * context, so each app provides its own adapter behind the same surface:
 *
 * - start: HTMLAudioElement pool controller
 *   (apps/start/src/lib/audio-chunk-player/player.tsx) — provides a handle
 *   directly (it has extra web-only snapshot fields).
 * - mobile: expo-audio pool (apps/mobile/src/lib/audio-pool-player-expo.ts)
 *   — mounts `AudioPoolPlayerInstanceProvider` with the created instance.
 *
 * The handle is the *render-facing* shape: the latest snapshot spread flat
 * (so `player.isPlaying` is reactive) plus the imperative methods.
 */

import {
  createContext,
  type PropsWithChildren,
  use,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import {
  defaultAudioPoolSnapshot,
  type AudioPoolPlayer,
  type AudioPoolSnapshot,
} from './pool-player';

/** The player's imperative methods only — no reactive snapshot fields.
 * Derived from the adapter contract so a method added there reaches the
 * handle by construction. Identity-stable across snapshot ticks, so leaf
 * consumers that just need `getPositionMs` (highlight managers, seek bars)
 * can read the player without subscribing to the per-tick snapshot. */
export type AudioPoolPlayerMethods = Omit<
  AudioPoolPlayer,
  'subscribe' | 'getSnapshot' | 'destroy'
>;

export type AudioPoolPlayerHandle = AudioPoolSnapshot & AudioPoolPlayerMethods;

export const AudioPoolPlayerContext =
  createContext<AudioPoolPlayerHandle | null>(null);

export function useAudioPoolPlayerContext(): AudioPoolPlayerHandle {
  const ctx = use(AudioPoolPlayerContext);
  if (!ctx)
    throw new Error(
      'useAudioPoolPlayerContext must be used within an AudioPoolPlayer provider',
    );
  return ctx;
}

export const AudioPoolPlayerMethodsContext =
  createContext<AudioPoolPlayerMethods | null>(null);

export function useAudioPoolPlayerMethods(): AudioPoolPlayerMethods {
  const ctx = use(AudioPoolPlayerMethodsContext);
  if (!ctx)
    throw new Error(
      'useAudioPoolPlayerMethods must be used within an AudioPoolPlayer provider',
    );
  return ctx;
}

/**
 * Provider for apps whose adapter already implements the `AudioPoolPlayer`
 * interface (subscribe/getSnapshot + methods). Owns the subscribe → snapshot
 * → handle plumbing and destroys the instance on unmount.
 */
export function AudioPoolPlayerInstanceProvider({
  makePlayer,
  children,
}: PropsWithChildren & {
  /** Called once on first render; the instance lives until unmount. */
  makePlayer: () => AudioPoolPlayer;
}) {
  const [player] = useState(makePlayer);

  const snapshot = useSyncExternalStore(
    player.subscribe,
    player.getSnapshot,
    () => defaultAudioPoolSnapshot,
  );

  // Seeks are counted here, not per adapter, so no adapter can forget the
  // paused-seek resample contract (`AudioPoolSnapshot.seekCount`). Every seek
  // flows through the wrapped method below — the handle is the only way in.
  const [seekCount, setSeekCount] = useState(0);

  // Methods only — identity stable across snapshot ticks so `getPositionMs`
  // consumers don't re-render 10x/s.
  const methods: AudioPoolPlayerMethods = useMemo(
    () => ({
      load: player.load,
      prewarmLoad: player.prewarmLoad,
      play: player.play,
      pause: player.pause,
      togglePlayPause: player.togglePlayPause,
      stop: player.stop,
      seek: (seconds: number) => {
        player.seek(seconds);
        setSeekCount((count) => count + 1);
      },
      getPositionSec: player.getPositionSec,
      getPositionMs: player.getPositionMs,
      setRate: player.setRate,
      setVolume: player.setVolume,
      mute: player.mute,
      unmute: player.unmute,
      ...(player.updateNowPlaying === undefined
        ? {}
        : { updateNowPlaying: player.updateNowPlaying }),
      ...(player.setLockScreenCommandHandler === undefined
        ? {}
        : { setLockScreenCommandHandler: player.setLockScreenCommandHandler }),
    }),
    [player],
  );

  const value: AudioPoolPlayerHandle = useMemo(
    () => ({ ...snapshot, ...methods, seekCount }),
    [snapshot, methods, seekCount],
  );

  useEffect(
    () => () => {
      player.destroy();
    },
    [player],
  );

  return (
    <AudioPoolPlayerMethodsContext value={methods}>
      <AudioPoolPlayerContext value={value}>{children}</AudioPoolPlayerContext>
    </AudioPoolPlayerMethodsContext>
  );
}
